'use client';

import { FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import Supercluster, { ClusterProperties } from 'supercluster';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useMapViewport } from './use-map-viewport';

export function useSupercluster<T extends GeoJsonProperties>(
  geojson: FeatureCollection<Point, T>,
  superclusterOptions: Supercluster.Options<T, ClusterProperties>,
) {
  const optionsRef = useRef(superclusterOptions);

  // create the clusterer and keep it stable
  const clusterer = useMemo(() => {
    return new Supercluster(optionsRef.current);
  }, []); // Remove dependency on superclusterOptions to prevent recreation

  // version-number for the data loaded into the clusterer
  // (this is needed to trigger updating the clusters when data was changed)
  const [version, dataWasUpdated] = useReducer((x: number) => x + 1, 0);
  const lastGeojsonRef = useRef<string>('');

  // when data changes, load it into the clusterer
  useEffect(() => {
    // Create a stable reference for geojson comparison
    const geojsonString = JSON.stringify(geojson.features.map((f) => f.id).sort());

    // Only reload if the actual data has changed (not just reference)
    if (lastGeojsonRef.current !== geojsonString) {
      clusterer.load(geojson.features);
      dataWasUpdated();
      lastGeojsonRef.current = geojsonString;
    }
  }, [clusterer, geojson]);

  // get bounding-box and zoomlevel from the map
  const { bbox, zoom } = useMapViewport();

  // retrieve the clusters within the current viewport with additional memoization
  const clusters = useMemo(() => {
    // don't try to read clusters before data was loaded into the clusterer (version===0),
    // otherwise getClusters will crash
    if (!clusterer || version === 0) return [];

    // Don't render clusters until we have a meaningful zoom level (> 0)
    // This prevents showing incorrect clusters on initial render
    if (zoom <= 0) return [];

    try {
      return clusterer.getClusters(bbox, Math.floor(zoom));
    } catch (error) {
      console.warn('Error getting clusters:', error);
      return [];
    }
  }, [version, clusterer, bbox, zoom]);

  // create callbacks to expose supercluster functionality outside of this hook
  const getChildren = useCallback((clusterId: number) => clusterer.getChildren(clusterId), [clusterer]);

  // note: here, the paging that would be possible is disabled; we found this
  // has no significant performance impact when it's just used in a click event handler.
  const getLeaves = useCallback((clusterId: number) => clusterer.getLeaves(clusterId, Infinity), [clusterer]);

  const getClusterExpansionZoom = useCallback(
    (clusterId: number) => clusterer.getClusterExpansionZoom(clusterId),
    [clusterer],
  );

  return {
    clusters,
    getChildren,
    getLeaves,
    getClusterExpansionZoom,
  };
}
